import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import type { TokenInput } from "@jkast90/kast-common/auth";
import { layoutOf, overridesFor } from "../emulator/keymap";

// The same UI runs as the website and inside the desktop app (desktop/app,
// Tauri). There PlayStation, PSP and Xbox games come from a folder on the Mac
// and play in their own native window, through these commands; Game Boy and
// N64 games play in the web player, in the app's window, as on the website.

export const isDesktop = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

/** Where games and saves live when not in the cloud, as the text says it: the app isn't a browser. */
export const here = isDesktop ? "this Mac" : "this browser";
export const Here = isDesktop ? "This Mac" : "This browser";

export interface DesktopGame {
  name: string;
  path: string;
  system: "psx" | "psp" | "xbox";
  size: number;
}

/** The consoles the desktop app plays natively rather than in the web player. */
export const NATIVE_SYSTEMS = ["psx", "psp", "xbox"] as const;
export const isNative = (system: string | null): boolean => (NATIVE_SYSTEMS as readonly string[]).includes(system ?? "");

export interface Picked {
  /** Picked files that aren't games CloudBoy plays. */
  refused: string[];
  /** The first picked Game Boy or N64 game, for the web player. */
  web: File | null;
  /** How many more of those were picked, which the player can't take at once. */
  moreWeb: number;
}

/** A file a folder scan turned up. */
export interface Found {
  path: string;
  name: string;
  size: number;
  kind: "cartridge" | "disc" | "archive";
  /** What it is, for the player: "Game Boy Advance (zip)", "Disc image". */
  what: string;
  /** Already in the games folder. */
  present: boolean;
}

/** How a game in its own window is going, from the app's `launch` events. */
interface LaunchEvent {
  path: string;
  name: string;
  system: string;
  launch: { kind: "step"; value: string } | { kind: "failed"; value: string } | { kind: "played"; value: number };
}

/** A transfer's progress, from the app's `transfer` events. */
export interface Transfer {
  phase: "hashing" | "uploading" | "downloading";
  fraction: number;
}

/** Runs `work` while passing on progress events for the game called `name`. */
async function withProgress<T>(name: string, onProgress: (t: Transfer) => void, work: () => Promise<T>): Promise<T> {
  const stop = await listen<Transfer & { name: string }>("transfer", ({ payload }) => {
    if (payload.name === name) onProgress({ phase: payload.phase, fraction: payload.fraction });
  });
  try {
    return await work();
  } finally {
    stop();
  }
}

// Play needs a game's path; the rest of the UI knows games by file name.
const paths = new Map<string, string>();

export const desktop = {
  async listGames(): Promise<DesktopGame[]> {
    const games = await invoke<DesktopGame[]>("list_games");
    for (const game of games) paths.set(game.name, game.path);
    return games;
  },
  /** Opens the file picker. PlayStation, PSP and Xbox games go into the games folder; a Game Boy or N64 one comes back to hand to the player. */
  async addGames(): Promise<Picked> {
    const { refused, web } = await invoke<{ refused: string[]; web: string[] }>("add_games");
    if (!web.length) return { refused, web: null, moreWeb: 0 };
    const bytes = await invoke<ArrayBuffer>("read_picked", { path: web[0] });
    const name = web[0].slice(web[0].lastIndexOf("/") + 1);
    return { refused, web: new File([bytes], name), moreWeb: web.length - 1 };
  },
  /** Opens a folder picker and lists the games under it, for the player to choose from. */
  scanFolder: () => invoke<Found[]>("scan_folder"),
  /** Adds chosen scanned files: discs are linked into the games folder; cartridges come back as files for browser storage. */
  async addFound(paths: string[]): Promise<{ files: File[]; added: number }> {
    const { web, added } = await invoke<{ web: string[]; added: number }>("add_found", { paths });
    const files = await Promise.all(
      web.map(async (path) => new File([await invoke<ArrayBuffer>("read_picked", { path })], path.slice(path.lastIndexOf("/") + 1))),
    );
    return { files, added };
  },
  /** Starts a game in its own window. Resolves once the window is up, passing on the steps before that; rejects with why it stopped. */
  async play(name: string, system: string, onStep: (step: string) => void): Promise<void> {
    const path = paths.get(name);
    if (!path) throw new Error(`${name} isn't in the games folder.`);
    let stop = () => {};
    try {
      await new Promise<void>((resolve, reject) => {
        listen<LaunchEvent>("launch", ({ payload }) => {
          if (payload.path !== path) return;
          const { kind, value } = payload.launch;
          if (kind === "failed") reject(new Error(value));
          else if (kind === "step" && value === "running") resolve();
          else if (kind === "step") onStep(value);
        })
          .then((unlisten) => {
            stop = unlisten;
            return invoke("play_game", { path, keymap: overridesFor({ game: name, console: layoutOf(system) }) });
          })
          .catch(reject);
      });
    } finally {
      stop();
    }
  },
  /** Hears the seconds played in game windows, as they come in; returns the way to stop. */
  onPlayed(handler: (game: { name: string; system: string }, seconds: number) => void): Promise<() => void> {
    return listen<LaunchEvent>("launch", ({ payload }) => {
      if (payload.launch.kind === "played") handler(payload, payload.launch.value);
    });
  },
  coverIndex: () => invoke<Record<string, string>>("cover_index"),
  /** Sends a game from the games folder to the cloud library, streamed from disk: discs are too big for the page. */
  uploadGame(name: string, system: string, token: string, onProgress: (t: Transfer) => void): Promise<"uploaded" | "already-there"> {
    const path = paths.get(name);
    if (!path) return Promise.reject(new Error(`${name} isn't in the games folder.`));
    return withProgress(name, onProgress, () => invoke("upload_game", { path, system, token }));
  },
  /** Fetches a cloud library game into the games folder. */
  downloadGame(rom: { id: string; name: string }, token: string, onProgress: (t: Transfer) => void): Promise<void> {
    return withProgress(rom.name, onProgress, () => invoke("download_game", { id: rom.id, name: rom.name, token }));
  },
  /** Signs in through the system browser; resolves once the login site hands the tokens back. */
  signIn: () => invoke<TokenInput>("sign_in"),
};

/** What to tell the player after a pick, if anything. */
export function pickedNote({ refused, moreWeb }: Picked): string | null {
  const notes = [];
  if (refused.length) notes.push(`${refused.join(", ")} ${refused.length === 1 ? "isn't a game" : "aren't games"} CloudBoy plays.`);
  if (moreWeb) notes.push("Game Boy and N64 games go in one at a time; add the others after this one.");
  return notes.join(" ") || null;
}

/** Raised by `signIn` (auth/session.ts) in the desktop app, where the window can't go to the login site. */
export const DESKTOP_SIGN_IN = "cloudboy:sign-in";

/** Where the web app serves box art; the desktop app loads it from there. */
export const COVERS_ORIGIN = "https://cloudboy.kast-dev.net";
